import React from "react";
import { Box, Typography, Link, IconButton, Container, Stack, Divider } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Facebook, LinkedIn, Instagram } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../theme";
import { EASE } from "../../styles/surfaces";
import goldAccreditation from "./Gold_RGB.png";
import ssipAccreditation from "./ssip-supplier-logo.png";

const navLinks = [
  { label: "About Us", to: "/about" },
  { label: "Privacy Policy", to: "/privacypolicy" },
  { label: "Cookie Policy", to: "/cookiepolicy" },
  { label: "Terms of Service", to: "/terms" },
  { label: "Contact", to: "/contact" },
];

const socialLinks = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=100063592419485", Icon: Facebook },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/aurimas-pudzemys-743869182/", Icon: LinkedIn },
  { label: "Instagram", href: "https://www.instagram.com/avfacades/", Icon: Instagram },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  // The footer stays dark in both colour modes. Its tokens are deliberately pinned to the
  // dark palette rather than following theme.palette.mode: the Constructionline badge is
  // gold ink on a fully transparent background and washes out on a light backing.
  const colors = tokens("dark");
  const hairline = alpha("#ffffff", 0.12);

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: colors.grey[900],
        color: "white",
        mt: "auto",
      }}
    >
      <Container maxWidth="xl">
        {/* Accreditations on one side, navigation and socials on the other. Below `md`
            the row becomes a centred stack in the same order. */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 4, md: 6 }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: { xs: 5, md: 6 } }}
        >
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            justifyContent="center"
            sx={{ flexShrink: 0 }}
          >
            <Box
              component="img"
              src={goldAccreditation}
              alt="Gold Accreditation"
              sx={{ height: { xs: 56, md: 64 }, width: "auto" }}
            />
            <Box
              component="img"
              src={ssipAccreditation}
              alt="SSIP Supplier Accreditation"
              sx={{ height: { xs: 56, md: 64 }, width: "auto" }}
            />
          </Stack>

          <Stack spacing={3} alignItems={{ xs: "center", md: "flex-end" }} sx={{ width: "100%" }}>
            <Box
              component="nav"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-end" },
                columnGap: { xs: 3, md: 4 },
                rowGap: 1.5,
              }}
            >
              {navLinks.map(({ label, to }) => (
                <Link
                  key={label}
                  component={RouterLink}
                  to={to}
                  onClick={() => window.scrollTo(0, 0)}
                  color="inherit"
                  underline="none"
                  sx={{
                    fontSize: "0.9375rem",
                    color: alpha("#ffffff", 0.78),
                    transition: `color .2s ${EASE}`,
                    "&:hover": { color: "#ffffff" },
                  }}
                >
                  {label}
                </Link>
              ))}
            </Box>

            <Stack direction="row" spacing={1}>
              {socialLinks.map(({ label, href, Icon }) => (
                <IconButton
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} (opens in a new tab)`}
                  sx={{
                    color: alpha("#ffffff", 0.82),
                    border: `1px solid ${hairline}`,
                    transition: `color .2s ${EASE}, background-color .2s ${EASE}, border-color .2s ${EASE}`,
                    "&:hover": {
                      color: "#ffffff",
                      backgroundColor: alpha("#ffffff", 0.1),
                      borderColor: alpha("#ffffff", 0.28),
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Divider sx={{ borderColor: hairline }} />

        <Typography
          variant="body2"
          sx={{
            py: 3,
            textAlign: { xs: "center", md: "left" },
            color: alpha("#ffffff", 0.62),
          }}
        >
          &copy; {currentYear} AV Facade Services. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
